import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import DefaultLayout from 'components/DefaultLayout';
import Spinner from 'components/Spinner';
import { Tabs } from 'antd';
import UpcomingSessions from 'components/Lectures/MySessionsTabs/UpcomingSessions/UpcomingSessions';
import PastSessions from 'components/Lectures/MySessionsTabs/PastSessions';
import { Search, Table, Title } from 'ui';
import FlexRow from 'components/FlexRow';
import { connect } from 'react-redux';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { useLecture } from 'hooks/Lectures/useLecture';
import { LectureProps } from 'components/Lectures/types';
import MySessionsCalendar from 'components/Lectures/MySessionsCalendar';
import '../styles.scss';
import { LectureAdminNavigation } from 'pages/Customers/CustomerUsersEdit/LectureTab';

const mapStateToProps = (state: any) => {
    return {
        session: state.session,
    };
};

export interface MyLecturesProps {
    session: any;
    adminView?: boolean;
    userTypeId?: number;
    setAdminSession?: React.Dispatch<React.SetStateAction<number>>;
    setAdminLecture?: React.Dispatch<React.SetStateAction<number>>;
    setAdminView?: React.Dispatch<React.SetStateAction<string>>;

    adminNavigation?: LectureAdminNavigation;
    setAdminNavigation?: React.Dispatch<React.SetStateAction<LectureAdminNavigation>>;
}

const MyLectures: React.FC<MyLecturesProps> = ({
    session,
    adminView = false,
    userTypeId,
    setAdminSession,
    setAdminLecture,
    setAdminView,

    adminNavigation,
    setAdminNavigation,
}) => {
    const [reload, setReload] = useState<boolean>(false);
    const [searchString, setSearchString] = useState<string>('');
    const [lecture, setLecture] = useState<LectureProps>({});
    const [activeTabKey, setActiveTabKey] = useState('');
    const [calendarDate, setCalendarDate] = useState('');

    const intl = useIntl();
    const params = useParams();
    const tab = params.tab;
    const navigate = useNavigate();
    const { getLectureById } = useLecture();

    const lecturesId = params.lecturesId ? +params.lecturesId : undefined;

    useEffect(() => {
        if (tab) {
            setActiveTabKey(tab);
        } else {
            if (!adminView) {
                setActiveTabKey('upcoming-sessions');
                navigate(`/my-lectures/upcoming-sessions`);
            } else {
                if (adminView && adminNavigation?.tab) {
                    setActiveTabKey(adminNavigation.tab);
                }
            }
        }
    }, [tab]);

    useEffect(() => {
        const loadlLecture = async () => {
            if (!!lecturesId) {
                const lecture = await getLectureById(lecturesId);
                if (lecture && lecture.id) {
                    setLecture(lecture);
                } else {
                    navigate('/my-lectures');
                }
            } else {
                navigate('/my-lectures');
            }
        };

        if (lecturesId) {
            loadlLecture();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lecturesId]);

    const renderContent = () => {
        if (session.active_user_type === 'SUPERVISION' && !lecturesId) {
            return (
                <DefaultLayout.PageContent>
                    <FlexRow
                        left={<FormattedMessage id={'student.my_lectures'} />}
                        right={<Search setSearchString={setSearchString} setReload={setReload} />}
                    />
                    <Table
                        columns={[
                            {
                                key: 'title',
                                title: intl.formatMessage({ id: 'general.lectures.title' }),
                                render: (record: any) => {
                                    return (
                                        <NavLink to={`/my-lectures/${record.id}`}>
                                            {record.title}
                                        </NavLink>
                                    );
                                },
                            },
                        ]}
                        url={'/api/v1/courses/lectures'}
                        setReload={setReload}
                        reload={reload}
                        rowSelection={false}
                        rowKey={'id'}
                        search={searchString}
                    />
                </DefaultLayout.PageContent>
            );
        }

        const tabChangeHandle = (tab: string) => {
            setActiveTabKey(tab);
            if (!adminView) {
                navigate(`/my-lectures/${tab}`);
            }
            if (adminView && adminNavigation && setAdminNavigation) {
                setAdminNavigation({ ...adminNavigation, tab: tab });
            }
        };

        return (
            <div className="flex gap-20">
                <Tabs
                    onChange={tabChangeHandle}
                    activeKey={activeTabKey}
                    destroyInactiveTabPane
                    className="w-100"
                    items={[
                        {
                            label: intl.formatMessage({ id: 'general.lectures.upcoming_sessions' }),
                            key: 'upcoming-sessions',
                            children: (
                                <UpcomingSessions
                                    lecture={lecture}
                                    adminView={adminView}
                                    userTypeId={userTypeId}
                                    setAdminSession={setAdminSession}
                                    setAdminLecture={setAdminLecture}
                                    setAdminView={setAdminView}
                                    calendarDate={calendarDate}
                                />
                            ),
                        },
                        {
                            label: intl.formatMessage({ id: 'general.lectures.past_sessions' }),
                            key: 'past-sessions',
                            children: (
                                <PastSessions
                                    lecture={lecture}
                                    adminView={adminView}
                                    userTypeId={userTypeId}
                                    setAdminSession={setAdminSession}
                                    setAdminLecture={setAdminLecture}
                                    setAdminView={setAdminView}
                                    calendarDate={calendarDate}
                                />
                            ),
                        },
                    ]}
                />

                <div className="w-35">
                    <>
                        <Title
                            className="my-session-calendar-title"
                            title={intl.formatMessage({ id: 'general.calendar' })}
                            withLine
                        />
                        <MySessionsCalendar
                            adminView={adminView}
                            calendarDate={calendarDate}
                            userTypeId={userTypeId}
                            setCalendarDate={setCalendarDate}
                            adminNavigation={adminNavigation}
                            setAdminNavigation={setAdminNavigation}
                        />
                    </>
                </div>
            </div>
        );
    };

    return (
        <Spinner spinning={false}>
            <DefaultLayout.PageLayout withoutPageLayout>
                {session.active_user_type && (
                    <div className="content-wrapper">
                        {lecturesId && lecture && (
                            <DefaultLayout.PageHeader
                                title={lecture.title ?? ''}
                                breadcrumb={[
                                    {
                                        name: intl.formatMessage({
                                            id: 'general.lectures.my_lectures',
                                        }),
                                        path: '/my-lectures',
                                    },
                                ]}
                            />
                        )}
                        {renderContent()}
                    </div>
                )}
            </DefaultLayout.PageLayout>
        </Spinner>
    );
};

export default connect(mapStateToProps)(MyLectures);
